import {
    Box, 
    Typography,
    useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ProgressBar from '../../components/progressBar';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import {
    useCallback, useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import Charts from '../../components/charts';
import dataDogLogger from '../../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, 
    TypographyWeights
} from '../../../../figmaTheme';
import {
    useCookies 
} from 'react-cookie';

type Props = {
    position: number
};

export const Rings = ({
    position
}: Props): JSX.Element => {
    const [cookies] = useCookies();
    const formContext = useContext(DiagnosticFormContext);

    const endGoalAnswer = useMemo(() => formContext.answers.endGoal, [formContext.answers.endGoal]);
    const targetDrinksAnswer = useMemo(() => formContext.answers.targetTotalDrinks, [formContext.answers.targetTotalDrinks]);
    const drinksAnswer = useMemo(() => formContext.answers.totalDrinksBaseline, [formContext.answers.totalDrinksBaseline]);
    const targetDryDaysAnswer = useMemo(() => formContext.answers.targetTotalDryDays, [formContext.answers.targetTotalDryDays]);
    const dryDaysAnswer = useMemo(() => formContext.answers.totalDryDaysBaseline, [formContext.answers.totalDryDaysBaseline]);
    const customTargetsAnswer = useMemo(() => formContext.answers.customTargets, [formContext.answers.customTargets]);
    const extraNightsOfSleep = useMemo(() => formContext.answers.extraNightsOfSleep, [formContext.answers.extraNightsOfSleep]);
    const dollarsSaved = useMemo(() => formContext.answers.dollarsSaved, [formContext.answers.dollarsSaved]);
    const caloriesAvoided = useMemo(() => formContext.answers.caloriesAvoided, [formContext.answers.caloriesAvoided]);

    const [titleHeight, setTitleHeight] = useState(0);
    const [subtitleHeight, setSubtitleHeight] = useState(0);
    const [direction, setDirection] = useState(formContext.screenDirection);
    const [skipAnimation, setSkipAnimation] = useState(direction === 'right');
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            placeItems: 'center'
        },
        title: {
            width: '100%',
            fontWeight: TypographyWeights.BOLD,
            marginBottom: '16px',
            transition: 'opacity 0.5s'
        },
        chartBox: {
            display: 'flex',
            flexDirection: 'column',
            placeItems: 'center',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`,
        },
        subtitle: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`,
        },
        subtitle2: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards 0s`,
        },
        sleepCircle: {
            transform: 'scale(0)',
            transformOrigin: '74.5px 74.5px',
            transition: 'opacity 0.5s',
            animation: `$grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`,
        },
        sleepCircle2: {
            transform: 'scale(1)',
            transition: 'opacity 0.5s',
            transformOrigin: '74.5px 74.5px'
        },
        sleep: {
            fontFamily: 'Poppins',
            fontSize: '32px',
            lineHeight: '40px',
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.neutral.main,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        sleep2: {
            fontFamily: 'Poppins',
            fontSize: '32px',
            lineHeight: '40px',
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.neutral.main
        },
        sleepText: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            lineHeight: '16px',
            color: theme.palette.neutral.main,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        sleepText2: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            lineHeight: '16px',
            color: theme.palette.neutral.main
        },
        sleepIcon: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        costCircle: {
            transform: 'scale(0)',
            transformOrigin: '232.5px 142.5px',
            animation: `$grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        costCircle2: {
            transform: 'scale(1)',
        },
        cost: {
            fontFamily: 'Poppins',
            fontSize: '32px',
            lineHeight: '40px',
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.neutral.main,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        cost2: {
            fontFamily: 'Poppins',
            fontSize: '32px',
            lineHeight: '40px',
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.neutral.main,
        },
        costText: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            lineHeight: '16px',
            color: theme.palette.neutral.main,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        costText2: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            lineHeight: '16px',
            color: theme.palette.neutral.main,
        },
        costIcon: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        caloriesCircle: {
            transform: 'scale(0)',
            transformOrigin: '90.5px 246.5px',
            transition: 'opacity 0.5s',
            animation: `$grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.5}s`,
        },
        caloriesCircle2: {
            transform: 'scale(1)',
            transition: 'opacity 0.5s'
        },
        calories: {
            fontFamily: 'Poppins',
            fontSize: '32px',
            lineHeight: '40px',
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.neutral.main,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 3}s`,
        },
        calories2: {
            fontFamily: 'Poppins',
            fontSize: '32px',
            lineHeight: '40px',
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.neutral.main,
        },
        caloriesText: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            lineHeight: '16px',
            color: theme.palette.neutral.main,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 3}s`,
        },
        caloriesText2: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            lineHeight: '16px',
            color: theme.palette.neutral.main,
        },
        caloriesIcon: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 3}s`,
        },
        fadeOut: {
            opacity: 0
        },
        fadeIn: {
            opacity: 1,
            transition: 'opacity 0.5s 0.5s'
        },
        svg: {
            position: 'absolute',
            width: '100%',
            transform: `translate(0, ${subtitleHeight + 40}px)`,
        },
        move: {
            // animation: `$move ${skipAnimation ? 0 : 1}s forwards ${skipAnimation ? 0 : 0.5}s`,
        },
        moved: {
            // transform: `translate(0, ${subtitleHeight - 12}px)`
        },
        unmove: {
            // animation: `$unmove ${skipAnimation ? 0 : 1}s forwards 0s`,
        },
        scale: {
            transformOrigin: '232.5px 142.5px',
            animation: `$scale ${skipAnimation ? 0 : 1}s forwards ${skipAnimation ? 0 : 0.5}s`,
        },
        scaled: {
            transformOrigin: '232.5px 142.5px',
            transform: 'scale(1.55) translate(-50px, 0)'
        },
        unscale: {
            transformOrigin: '232.5px 142.5px',
            animation: `$unscale ${skipAnimation ? 0 : 1}s forwards 0s`,
        },
        hidden: {
            opacity: 0,
            margin: 0,
            transform: `translate(0, -${titleHeight + subtitleHeight + 16}px)`,
        },
        faded: {
            opacity: 0,
        },
        visible: {
            opacity: 0,
            fontSize: '20px',
            lineHeight: '28px',
            transform: `translate(0, -${titleHeight + subtitleHeight + 16}px)`,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        shown: {
            margin: 0,
            opacity: 1,
            transform: `translate(0, -${titleHeight + subtitleHeight + 16}px)`
        },
        finalScreen: {
            display: 'flex',
            flexDirection: 'column',
            placeItems: 'center',
            opacity: 0,
            animation: '$fadeIn 0.5s forwards 0.5s'
        },
        '@keyframes grow': {
            '0%': {
                transform: 'scale(0)' 
            },
            '100%': {
                transform: 'scale(1)' 
            },
        },
        '@keyframes scale': {
            '0%': {
                transform: 'scale(1) translate(0)' 
            },
            '100%': {
                transform: 'scale(1.55) translate(-50px, 0)' 
            },
        },
        '@keyframes unscale': {
            '0%': {
                transform: 'scale(1.55) translate(-50px, 0)' 
            },
            '100%': {
                transform: 'scale(1) translate(0)' 
            },
        },
        '@keyframes move': {
            '0%': {
                transform: `translate(0, ${subtitleHeight + 40}px)` 
            },
            '100%': {
                transform: `translate(0, ${subtitleHeight - 12}px)` 
            },
        },
        '@keyframes unmove': {
            '0%': {
                transform: `translate(0, ${subtitleHeight - 12}px)` 
            },
            '100%': {
                transform: `translate(0, ${subtitleHeight + 40}px)` 
            },
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0 
            },
            '100%': {
                opacity: 1 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: 1 
            },
            '100%': {
                opacity: 0 
            },
        }
    })), [titleHeight, subtitleHeight, skipAnimation]);

    const classes = useStyles();
    const theme = useTheme();
    const dollarValue = useMemo(() => {
        const amount: string = dollarsSaved.slice(1);
        let convertedAmount = 0;
        if (amount.endsWith('k')) {
            convertedAmount = parseFloat(amount.slice(0, -1)) * 1000;
        } else if (amount.endsWith('m')) {
            convertedAmount = parseFloat(amount.slice(0, -1)) * 1000000;
        } else {
            convertedAmount = parseInt(amount);
        }
        return convertedAmount;
    }, [dollarsSaved]);
    const [startTime, setStartTime] = useState(formContext.startTime);
    const [step, setStep] = useState(formContext.screenDirection === 'right' ? dollarValue > 100 ? 2 : 1 : 0);

    const onClick = () => {
        if (step === 2) {
            if (!skipAnimation) setSkipAnimation(true);
            else formContext.goNext();
        } else if (step === 0) {
            setStep(1);
            setSkipAnimation(false);
        } else if (step === 1 && dollarValue > 100) {
            if (!skipAnimation) setSkipAnimation(true);
            else {
                setStep(2);
                setSkipAnimation(false);
            }
        } else {
            if (!skipAnimation) setSkipAnimation(true);
            else formContext.goNext();
        }
        setDirection('left');
        setStartTime(new Date().getTime());
    };

    const onBackClick = () => {
        if (step === 0) formContext.goBack();
        else if (step === 1) {
            clearTimeout(timeout1.current);
        } else if (step === 2) {
            clearTimeout(timeout2.current);
        }
        setStep(step - 1);
        setDirection('right');
        setStartTime(null);
        setSkipAnimation(false);
    };

    const timeout1 = useRef(null);
    const timeout2 = useRef(null);

    useEffect(() => {
        let trackingName = 'first year outlook';

        if (step === 1) {
            trackingName = 'first year summary';
            timeout1.current = setTimeout(() => {
                setSkipAnimation(true);
            }, 3500);
        } else if (step === 2) {
            trackingName = 'first year savings';
            timeout2.current = setTimeout(() => {
                setSkipAnimation(true);
            }, 1500);
        }
        formContext.fireTrackingEvents(formContext, trackingName, cookies);
    }, [step]);

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;

        let url = 'first year outlook';

        if (step === 1) {
            url = 'first year summary';
        } else if (step === 2) {
            url = 'first year savings';
        }

        dataDogLogger(
            'info',
            `Diagnostic screen ${url} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url,
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    const title = useRef(null);
    const subtitle = useRef(null);

    useEffect(() => {
        if (!title.current) return;
        setTitleHeight(title.current.clientHeight);
    }, [title]);

    useEffect(() => {
        if (!subtitle.current) return;
        setSubtitleHeight(subtitle.current.clientHeight);
    }, [subtitle]);

    const getYearlyImpact = useCallback(() => {
        const dryDayGain = Math.floor((52 / 12 * 3) * (dryDaysAnswer + 1) + (52 / 12 * 9) * targetDryDaysAnswer) - (dryDaysAnswer * 52);
        let drinkReduction = (drinksAnswer * 52) - Math.floor((52 / 12 * 3) * drinksAnswer * 0.68 + (52 / 12 * 9) * drinksAnswer * 0.5);

        if (customTargetsAnswer === 'true') {
            drinkReduction = Math.floor((52 / 12 * 3) * drinksAnswer * 0.68 + (52 / 12 * 9) * targetDrinksAnswer);
        } else if (endGoalAnswer === 'Complete Zero') {
            drinkReduction = Math.floor((52 / 12 * 3) * drinksAnswer * 0.68 + (52 / 12 * 3) * drinksAnswer * 0.3);
        }

        return {
            drinkReduction,
            dryDayGain
        };
    }, [drinksAnswer, targetDrinksAnswer, dryDaysAnswer, targetDryDaysAnswer, customTargetsAnswer, endGoalAnswer]);

    const {
        drinkReduction, dryDayGain 
    } = useMemo(() => getYearlyImpact(), [getYearlyImpact]);

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={onBackClick}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position + step}
                    sectionKey={'plan'}
                />
                <Box className={classes.scrollableContainer}>
                    <Typography
                        ref={title}
                        variant={TypographyVariants.H3}
                        className={`${classes.title} ${step === 2 ? classes.fadeOut : classes.fadeIn}`}
                        dangerouslySetInnerHTML={{
                            __html: 'Your first year with Sunnyside' 
                        }}
                    />
                    <Box className={classes.content}>
                        {step === 0 && (
                            <Box className={classes.chartBox}>
                                <Typography
                                    ref={subtitle}
                                    variant={TypographyVariants.H6}
                                    className={classes.subtitle}
                                    dangerouslySetInnerHTML={{
                                        __html: 'Small changes add up to a huge impact. By sticking with Sunnyside, here\'s the full impact you can expect a year from now:' 
                                    }}
                                />
                                <Charts
                                    drinksAnswer={drinksAnswer}
                                    targetDrinksAnswer={targetDrinksAnswer}
                                    daysAnswer={dryDaysAnswer}
                                    targetDaysAnswer={targetDryDaysAnswer}
                                    drinkReduction={drinkReduction}
                                    dryDayGain={dryDayGain}
                                />
                            </Box>
                        )}
                        {step === 1 && (
                            <>
                                <Typography
                                    ref={subtitle}
                                    variant={TypographyVariants.H6}
                                    className={classes.subtitle}
                                    dangerouslySetInnerHTML={{
                                        __html: `The cumulative impact of cutting ${drinkReduction} drinks and adding ${dryDayGain} dry days will have a profound impact on your health and lifestyle.` 
                                    }}
                                />
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.hidden}
                                    dangerouslySetInnerHTML={{
                                        __html: `By helping you save <span style="font-weight:${TypographyWeights.BOLD};">${dollarsSaved} per year</span>, the Sunnyside membership ${dollarValue > 200 ? `pays for itself ${Math.floor(dollarValue / 100)} times over in just the first year!` : 'more than pays for itself.'}` 
                                    }}
                                />
                                <svg
                                    className={`${classes.svg} ${direction === 'right' ? classes.unmove : null}`}
                                    width="307"
                                    height="321"
                                    viewBox="0 0 307 321"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g className={classes.sleepCircle}>
                                        <circle
                                            cx="74.5"
                                            cy="74.5"
                                            r="68.5"
                                            fill={theme.palette.tertiary.light}
                                            stroke={theme.palette.tertiary['200']}
                                            strokeWidth="12"
                                        />
                                        <text
                                            className={classes.sleep}
                                            x="74.5"
                                            y="72"
                                            fontSize="32"
                                            fontWeight={TypographyWeights.BOLD}
                                            fill={theme.palette.neutral.main}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            {extraNightsOfSleep}
                                        </text>
                                        <text
                                            className={classes.sleepText}
                                            x="74.5"
                                            y="100"
                                            textLength="83px"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Better Nights of
                                        </text>
                                        <text
                                            className={classes.sleepText}
                                            x="74.5"
                                            y="115"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Sleep
                                        </text>
                                        <path
                                            className={classes.sleepIcon}
                                            d="M72.5522 41.0798C77.762 41.0798 82 36.7648 82 31.4603C82 26.8498 78.7743 22.8783 74.3338 22.0125C74.0166 21.9507 73.7062 22.1225 73.5847 22.4248C73.4565 22.7203 73.5577 23.0707 73.8277 23.2562C75.7847 24.6235 76.9589 26.8841 76.9589 29.3028C76.9589 33.3361 73.7332 36.6205 69.7651 36.6205C68.0645 36.6205 66.4112 36.0021 65.1155 34.8821C64.8725 34.6691 64.5149 34.6622 64.2584 34.8615C64.0087 35.0608 63.9278 35.4181 64.0695 35.7066C65.6756 39.0185 68.9283 41.0798 72.5522 41.0798Z"
                                            fill={theme.palette.tertiary.main}
                                        />
                                    </g>
                                    <g className={direction === 'right' ? classes.unscale : classes.costCircle}>
                                        <circle
                                            cx="232.5"
                                            cy="142.5"
                                            r="68.5"
                                            fill={theme.palette.tertiary.light}
                                            stroke={theme.palette.tertiary['200']}
                                            strokeWidth="12"
                                        />
                                        <text
                                            className={classes.cost}
                                            x="232.5"
                                            y="138"
                                            fontSize="32"
                                            fontWeight={TypographyWeights.BOLD}
                                            fill={theme.palette.neutral.main}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            {dollarsSaved}
                                        </text>
                                        <text
                                            className={classes.costText}
                                            x="232.5"
                                            y="170"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Yearly Savings
                                        </text>
                                        <g
                                            className={classes.costIcon}
                                            clipPath="url(#clip0_1036_48929)"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M231 92.5751C229.746 92.5751 228.729 93.5916 228.729 94.8456C228.729 96.0995 229.746 97.116 231 97.116C232.254 97.116 233.27 96.0995 233.27 94.8456C233.27 93.5916 232.254 92.5751 231 92.5751ZM227.379 94.8456C227.379 92.846 229 91.2251 231 91.2251C232.999 91.2251 234.62 92.846 234.62 94.8456C234.62 96.8451 232.999 98.466 231 98.466C229 98.466 227.379 96.8451 227.379 94.8456Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M231 93.4342C231.373 93.4342 231.675 93.7364 231.675 94.1092V95.5819C231.675 95.9547 231.373 96.2569 231 96.2569C230.627 96.2569 230.325 95.9547 230.325 95.5819V94.1092C230.325 93.7364 230.627 93.4342 231 93.4342Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M226.803 103.246C226.931 103.094 227.12 103.007 227.318 103.007H234.682C234.88 103.007 235.069 103.094 235.197 103.246C235.325 103.397 235.38 103.597 235.348 103.793L234.611 108.211C234.557 108.537 234.275 108.775 233.945 108.775H228.055C227.725 108.775 227.443 108.537 227.389 108.211L226.652 103.793C226.62 103.597 226.675 103.397 226.803 103.246ZM228.115 104.357L228.626 107.425H233.374L233.885 104.357H228.115Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M228.126 100.27C227.47 99.9739 226.743 99.8732 226.031 99.9804C225.615 100.043 225.215 100.175 224.846 100.369C225.377 100.649 225.972 100.798 226.582 100.798C226.954 100.798 227.257 101.1 227.257 101.473C227.257 101.845 226.955 102.148 226.582 102.148C225.343 102.148 224.147 101.695 223.219 100.874L222.647 100.368L223.22 99.8622C223.954 99.2138 224.861 98.7912 225.83 98.6454C226.799 98.4995 227.789 98.6366 228.682 99.0401C229.575 99.4436 230.332 100.096 230.863 100.92C231.394 101.743 231.676 102.703 231.675 103.683C231.675 104.055 231.372 104.357 230.999 104.357C230.626 104.356 230.325 104.054 230.325 103.681C230.326 102.961 230.119 102.256 229.729 101.651C229.339 101.046 228.782 100.567 228.126 100.27Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M233.318 99.0401C234.211 98.6366 235.201 98.4995 236.17 98.6454C237.139 98.7912 238.046 99.2138 238.78 99.8622L239.353 100.368L238.781 100.874C237.853 101.695 236.657 102.148 235.418 102.148C235.045 102.148 234.743 101.845 234.743 101.473C234.743 101.1 235.046 100.798 235.418 100.798C236.028 100.798 236.623 100.649 237.154 100.369C236.786 100.175 236.385 100.043 235.969 99.9804C235.258 99.8732 234.53 99.9739 233.874 100.27C233.218 100.567 232.661 101.046 232.271 101.651C231.881 102.256 231.674 102.961 231.675 103.681C231.675 104.054 231.374 104.356 231.001 104.357C230.628 104.357 230.325 104.055 230.325 103.683C230.324 102.703 230.606 101.743 231.137 100.92C231.668 100.096 232.425 99.4436 233.318 99.0401Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_1036_48929">
                                                <rect
                                                    width="18"
                                                    height="18"
                                                    fill={theme.palette.white}
                                                    transform="translate(222 91)"
                                                />
                                            </clipPath>
                                        </defs>
                                    </g>
                                    <g className={classes.caloriesCircle}>
                                        <circle
                                            cx="90.5"
                                            cy="246.5"
                                            r="68.5"
                                            fill={theme.palette.tertiary.light}
                                            stroke={theme.palette.tertiary['200']}
                                            strokeWidth="12"
                                        />
                                        <text
                                            className={classes.calories}
                                            x="90.5"
                                            y="240"
                                            fontSize="32"
                                            fontWeight={TypographyWeights.BOLD}
                                            fill={theme.palette.neutral.main}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            {caloriesAvoided}
                                        </text>
                                        <text
                                            className={classes.caloriesText}
                                            x="90.5"
                                            y="270"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Calories
                                        </text>
                                        <text
                                            className={classes.caloriesText}
                                            x="90.5"
                                            y="285"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Avoided
                                        </text>
                                        <path
                                            className={classes.caloriesIcon}
                                            d="M91.7943 202.224C91.7943 202.224 92.5708 197.656 89.4024 195.673C89.3552 196.458 89.143 197.224 88.7796 197.921C88.4162 198.618 87.9099 199.231 87.2936 199.719C85.9405 200.909 83.3955 203.584 83.4226 206.436C83.4129 207.655 83.744 208.853 84.3787 209.894C85.0134 210.935 85.9264 211.778 87.0146 212.327C87.0535 211.777 87.2019 211.24 87.451 210.747C87.7002 210.255 88.0451 209.817 88.4656 209.46C88.8219 209.186 89.1201 208.843 89.3428 208.453C89.5655 208.063 89.7082 207.632 89.7626 207.185C90.6941 207.681 91.4811 208.409 92.0467 209.3C92.6123 210.19 92.9371 211.212 92.9893 212.266V212.279C94.0164 211.809 94.8934 211.063 95.5231 210.125C96.1528 209.187 96.5108 208.094 96.5574 206.965C96.7822 204.286 95.3146 200.642 94.0124 199.452C93.5208 200.549 92.7574 201.503 91.7943 202.224Z"
                                            fill={theme.palette.tertiary.main}
                                        />
                                    </g>
                                </svg>
                            </>
                        )}
                        {step === 2 && (
                            <Box className={direction === 'right' ? classes.finalScreen : classes.content}>
                                <Typography
                                    ref={subtitle}
                                    variant={TypographyVariants.H6}
                                    className={direction === 'right' ? classes.faded : classes.subtitle2}
                                    dangerouslySetInnerHTML={{
                                        __html: `The cumulative impact of cutting ${drinkReduction} drinks and adding ${dryDayGain} dry days will have a profound impact on your health and lifestyle.` 
                                    }}
                                />
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={direction === 'right' ? classes.shown : classes.visible}
                                    dangerouslySetInnerHTML={{
                                        __html: `By helping you save <span style="font-weight:${TypographyWeights.BOLD};">${dollarsSaved} per year</span>, the Sunnyside membership ${dollarValue > 200 ? `pays for itself ${Math.floor(dollarValue / 100)} times over in just the first year!` : 'more than pays for itself.'}` 
                                    }}
                                />
                                <svg
                                    className={`${classes.svg} ${direction === 'right' ? classes.moved : classes.move}`}
                                    width="307"
                                    height="321"
                                    viewBox="0 0 307 321"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g className={`${classes.sleepCircle2} ${direction === 'right' ? classes.faded : classes.fadeOut}`}>
                                        <circle
                                            cx="74.5"
                                            cy="74.5"
                                            r="68.5"
                                            fill={theme.palette.tertiary.light}
                                            stroke={theme.palette.tertiary['200']}
                                            strokeWidth="12"
                                        />
                                        <text
                                            className={classes.sleep2}
                                            x="74.5"
                                            y="72"
                                            fontSize="32"
                                            fontWeight={TypographyWeights.BOLD}
                                            fill={theme.palette.neutral.main}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            {extraNightsOfSleep}
                                        </text>
                                        <text
                                            className={classes.sleepText2}
                                            x="74.5"
                                            y="100"
                                            textLength="83px"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            More Nights of
                                        </text>
                                        <text
                                            className={classes.sleepText2}
                                            x="74.5"
                                            y="115"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Sleep
                                        </text>
                                        <path
                                            d="M72.5522 41.0798C77.762 41.0798 82 36.7648 82 31.4603C82 26.8498 78.7743 22.8783 74.3338 22.0125C74.0166 21.9507 73.7062 22.1225 73.5847 22.4248C73.4565 22.7203 73.5577 23.0707 73.8277 23.2562C75.7847 24.6235 76.9589 26.8841 76.9589 29.3028C76.9589 33.3361 73.7332 36.6205 69.7651 36.6205C68.0645 36.6205 66.4112 36.0021 65.1155 34.8821C64.8725 34.6691 64.5149 34.6622 64.2584 34.8615C64.0087 35.0608 63.9278 35.4181 64.0695 35.7066C65.6756 39.0185 68.9283 41.0798 72.5522 41.0798Z"
                                            fill={theme.palette.tertiary.main}
                                        />
                                    </g>
                                    <g className={`${classes.costCircle2} ${direction === 'right' ? classes.scaled : classes.scale}`}>
                                        <circle
                                            cx="232.5"
                                            cy="142.5"
                                            r="68.5"
                                            fill={theme.palette.tertiary.light}
                                            stroke={theme.palette.tertiary['200']}
                                            strokeWidth="12"
                                        />
                                        <text
                                            className={classes.cost2}
                                            x="232.5"
                                            y="138"
                                            fontSize="32"
                                            fontWeight={TypographyWeights.BOLD}
                                            fill={theme.palette.neutral.main}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            {dollarsSaved}
                                        </text>
                                        <text
                                            className={classes.costText2}
                                            x="232.5"
                                            y="170"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Yearly Savings
                                        </text>
                                        <g clipPath="url(#clip0_1036_48929)">
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M231 92.5751C229.746 92.5751 228.729 93.5916 228.729 94.8456C228.729 96.0995 229.746 97.116 231 97.116C232.254 97.116 233.27 96.0995 233.27 94.8456C233.27 93.5916 232.254 92.5751 231 92.5751ZM227.379 94.8456C227.379 92.846 229 91.2251 231 91.2251C232.999 91.2251 234.62 92.846 234.62 94.8456C234.62 96.8451 232.999 98.466 231 98.466C229 98.466 227.379 96.8451 227.379 94.8456Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M231 93.4342C231.373 93.4342 231.675 93.7364 231.675 94.1092V95.5819C231.675 95.9547 231.373 96.2569 231 96.2569C230.627 96.2569 230.325 95.9547 230.325 95.5819V94.1092C230.325 93.7364 230.627 93.4342 231 93.4342Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M226.803 103.246C226.931 103.094 227.12 103.007 227.318 103.007H234.682C234.88 103.007 235.069 103.094 235.197 103.246C235.325 103.397 235.38 103.597 235.348 103.793L234.611 108.211C234.557 108.537 234.275 108.775 233.945 108.775H228.055C227.725 108.775 227.443 108.537 227.389 108.211L226.652 103.793C226.62 103.597 226.675 103.397 226.803 103.246ZM228.115 104.357L228.626 107.425H233.374L233.885 104.357H228.115Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M228.126 100.27C227.47 99.9739 226.743 99.8732 226.031 99.9804C225.615 100.043 225.215 100.175 224.846 100.369C225.377 100.649 225.972 100.798 226.582 100.798C226.954 100.798 227.257 101.1 227.257 101.473C227.257 101.845 226.955 102.148 226.582 102.148C225.343 102.148 224.147 101.695 223.219 100.874L222.647 100.368L223.22 99.8622C223.954 99.2138 224.861 98.7912 225.83 98.6454C226.799 98.4995 227.789 98.6366 228.682 99.0401C229.575 99.4436 230.332 100.096 230.863 100.92C231.394 101.743 231.676 102.703 231.675 103.683C231.675 104.055 231.372 104.357 230.999 104.357C230.626 104.356 230.325 104.054 230.325 103.681C230.326 102.961 230.119 102.256 229.729 101.651C229.339 101.046 228.782 100.567 228.126 100.27Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M233.318 99.0401C234.211 98.6366 235.201 98.4995 236.17 98.6454C237.139 98.7912 238.046 99.2138 238.78 99.8622L239.353 100.368L238.781 100.874C237.853 101.695 236.657 102.148 235.418 102.148C235.045 102.148 234.743 101.845 234.743 101.473C234.743 101.1 235.046 100.798 235.418 100.798C236.028 100.798 236.623 100.649 237.154 100.369C236.786 100.175 236.385 100.043 235.969 99.9804C235.258 99.8732 234.53 99.9739 233.874 100.27C233.218 100.567 232.661 101.046 232.271 101.651C231.881 102.256 231.674 102.961 231.675 103.681C231.675 104.054 231.374 104.356 231.001 104.357C230.628 104.357 230.325 104.055 230.325 103.683C230.324 102.703 230.606 101.743 231.137 100.92C231.668 100.096 232.425 99.4436 233.318 99.0401Z"
                                                fill={theme.palette.tertiary.main}
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_1036_48929">
                                                <rect
                                                    width="18"
                                                    height="18"
                                                    fill={theme.palette.white}
                                                    transform="translate(222 91)"
                                                />
                                            </clipPath>
                                        </defs>
                                    </g>
                                    <g className={`${classes.caloriesCircle2} ${direction === 'right' ? classes.faded : classes.fadeOut}`}>
                                        <circle
                                            cx="90.5"
                                            cy="246.5"
                                            r="68.5"
                                            fill={theme.palette.tertiary.light}
                                            stroke={theme.palette.tertiary['200']}
                                            strokeWidth="12"
                                        />
                                        <text
                                            className={classes.calories2}
                                            x="90.5"
                                            y="240"
                                            fontSize="32"
                                            fontWeight={TypographyWeights.BOLD}
                                            fill={theme.palette.neutral.main}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            {caloriesAvoided}
                                        </text>
                                        <text
                                            className={classes.caloriesText2}
                                            x="90.5"
                                            y="270"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Calories
                                        </text>
                                        <text
                                            className={classes.caloriesText2}
                                            x="90.5"
                                            y="285"
                                            fontSize="12"
                                            fill={theme.palette.black}
                                            alignmentBaseline='middle'
                                            textAnchor='middle'
                                        >
                                            Avoided
                                        </text>
                                        <path
                                            d="M91.7943 202.224C91.7943 202.224 92.5708 197.656 89.4024 195.673C89.3552 196.458 89.143 197.224 88.7796 197.921C88.4162 198.618 87.9099 199.231 87.2936 199.719C85.9405 200.909 83.3955 203.584 83.4226 206.436C83.4129 207.655 83.744 208.853 84.3787 209.894C85.0134 210.935 85.9264 211.778 87.0146 212.327C87.0535 211.777 87.2019 211.24 87.451 210.747C87.7002 210.255 88.0451 209.817 88.4656 209.46C88.8219 209.186 89.1201 208.843 89.3428 208.453C89.5655 208.063 89.7082 207.632 89.7626 207.185C90.6941 207.681 91.4811 208.409 92.0467 209.3C92.6123 210.19 92.9371 211.212 92.9893 212.266V212.279C94.0164 211.809 94.8934 211.063 95.5231 210.125C96.1528 209.187 96.5108 208.094 96.5574 206.965C96.7822 204.286 95.3146 200.642 94.0124 199.452C93.5208 200.549 92.7574 201.503 91.7943 202.224Z"
                                            fill={theme.palette.tertiary.main}
                                        />
                                    </g>
                                </svg>
                            </Box>
                        )}
                    </Box>
                    <Box
                        style={{
                            height: step === 0 ? '300px' : '700px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default Rings;
